import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * Independent Stays plan IS4: Stays → Billing (BE src/stays/routes/billing.js).
 * Every amount is integer cents from the server; nothing here computes a price.
 *
 *   account 'host'    a Stays-only account on a plan (per place / per check-in)
 *   account 'addon'   a Lobbero Desk customer with Stays: listing slots
 *   account 'preview' a Stays-only account with no plan yet ("Choose a plan")
 *   account 'bundle'  Desk + Stays at one price (IS6): no slots, every listing included
 */

export type StaysBillingAccount = 'host' | 'addon' | 'preview' | 'bundle' | 'none';
export type StaysPlanModel = 'per_listing' | 'per_checkin';

export interface StaysBillingPlan {
  code: string;
  name: string;
  product_line: string;
  model: StaysPlanModel | null;
  status: string;
  comped: boolean;
  trial_end: string | null;
  properties: number;
  cancel_at_period_end: boolean;
}

export interface StaysBillingUsage {
  period_start: string;
  period_end: string | null;
  checkins: number;
  estimated_cents: number;
  free_trial: boolean;
}

export interface StaysBilling {
  enabled: boolean;
  account: StaysBillingAccount;
  products: { desk: boolean; stays: boolean };
  plan: StaysBillingPlan | null;
  status: string;
  kind: 'addon' | 'usage_only' | null;
  pricing_model: string | null;
  comped: boolean;
  provider: string | null;
  currency: string;
  listing_quantity: number;
  listings_used: number;
  remaining: number;
  prices: {
    listing_cents: number | null;
    addon_cents: number | null;
    checkin_cents: number | null;
    confirmed: boolean;
  };
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  usage: StaysBillingUsage;
  preview: { cap: number; used: number } | null;
}

export interface StaysSlotQuote {
  current: number;
  target: number;
  delta: number;
  unit_cents: number;
  recurring_cents: number;
  prorated_cents: number;
  next_invoice_cents: number;
  currency: string;
  period_end: string | null;
}

export interface StaysInvoice {
  id: string;
  number: string | null;
  status: string;
  total_cents: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
}

export interface StaysConvertQuote {
  plan_code: string;
  plan_name: string;
  model: StaysPlanModel;
  properties: number;
  properties_used: number;
  unit_cents: number;
  monthly_cents: number;
  fee_cents: number;
  currency: string;
  trial_days: number;
  free_signup: boolean;
  due_today_cents: number;
  card_required: boolean;
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const base = `${apiRoute}/stays/billing`;

export const getStaysBilling = (): Promise<StaysBilling> => wrap(axios.get(base), 'Could not load Stays billing.');

export const getStaysInvoices = (): Promise<{ invoices: StaysInvoice[] }> =>
  wrap(axios.get(`${base}/invoices`), 'Could not load your invoices.');

export const previewSlots = (quantity: number): Promise<StaysSlotQuote> =>
  wrap(axios.post(`${base}/listings/preview`, { quantity }), 'Could not price that change.');

export const setSlots = (quantity: number, expectedNextInvoiceCents: number): Promise<StaysBilling> =>
  wrap(
    axios.post(`${base}/listings`, { quantity, expected_next_invoice_cents: expectedNextInvoiceCents }),
    'Could not change your listing slots.'
  );

export const cancelStays = (): Promise<StaysBilling> =>
  wrap(axios.post(`${base}/cancel`, {}), 'Could not stop paying for Stays.');

export const resumeStays = (): Promise<StaysBilling> =>
  wrap(axios.post(`${base}/resume`, {}), 'Could not keep paying for Stays.');

export const previewConvert = (planCode: string, properties: number): Promise<StaysConvertQuote> =>
  wrap(axios.post(`${base}/convert/preview`, { plan_code: planCode, properties }), 'Could not price that plan.');

export const setupConvert = (): Promise<{ client_secret: string; setup_intent_id: string; mode: 'setup' }> =>
  wrap(axios.post(`${base}/convert/setup`, {}), 'Could not open the card step.');

export const convertPreview = (
  planCode: string,
  properties: number,
  setupIntentId?: string | null
): Promise<StaysBilling & { converted: true }> =>
  wrap(
    axios.post(`${base}/convert`, {
      plan_code: planCode,
      properties,
      ...(setupIntentId ? { setup_intent_id: setupIntentId } : {}),
    }),
    'Could not start your plan.'
  );
